import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/**
 * `tailwind.config.ts`'s `fontSize` scale replaces Tailwind's default size
 * keys (`sm`/`lg`/...) with this project's own (`display`, `h1`..`h4`,
 * `body-lg`, `body`, `body-sm`, `caption`, `label`, `button`) — see that
 * file's own comment on why. Plain `tailwind-merge` doesn't know about
 * custom keys, so it can't tell `text-button` (a font *size*) apart from
 * `text-primary-foreground` (a text *color*) and treats them as the same
 * conflicting group, silently dropping whichever appears first in a class
 * string. That's not hypothetical — `Button`'s `size="sm"`/`"lg"` variants
 * add `text-body-sm`/`text-h3` alongside the color variant's
 * `text-{color}-foreground`, and tailwind-merge was dropping the color
 * class, rendering default-colored (near-black) button text instead of the
 * intended white — caught by actually inspecting a rendered button's
 * computed style, not by lint/typecheck, since dropped Tailwind classes
 * aren't a type error. This custom `font-size` classGroup entry is the
 * fix: it tells tailwind-merge these are size utilities, so they only
 * conflict with each other, never with an unrelated color class.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "font-size": [
        {
          text: ["display", "h1", "h2", "h3", "h4", "body-lg", "body", "body-sm", "caption", "label", "button"],
        },
      ],
    },
  },
});

/**
 * Combines conditional class names (clsx) and resolves conflicting Tailwind
 * utility classes in favor of the last one specified (tailwind-merge) — the
 * standard pairing Shadcn UI's generated components expect. It lives in
 * utils/, not lib/, per docs/architecture/frontend-architecture.md's own
 * distinction: lib/ wraps a third-party client (axios, a socket), utils/
 * holds pure helpers — `cn` has no client to wrap.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
